import { useEffect } from 'react';

import { appVersion } from '@/config';
import { useAuth } from '@/lib/auth';
import { warn } from '@/utils/logger';

import {
  resolveGuide,
  getVersionKey,
  hasSeenCurrentGuide,
  markGuideAsSeen,
} from '../config';
import { useOnboardingStore } from '../store/useOnboardingStore';

/**
 * Core onboarding hook.
 *
 * @param autoTrigger  When true, automatically shows the guide on first mount
 *                     if the user has not yet seen it for the current major version.
 *                     Pass `true` only from OnboardingProvider (once, globally).
 */
export const useOnboarding = (autoTrigger = false) => {
  const store = useOnboardingStore();
  const openGuide = useOnboardingStore((s) => s.open);
  const closeGuide = useOnboardingStore((s) => s.close);
  const user = useAuth();

  useEffect(() => {
    if (!autoTrigger) return;
    // Never auto-show the guide for unauthenticated users.
    if (!user) {
      closeGuide();
      return;
    }
    const versionKey = getVersionKey(appVersion);
    const guide = resolveGuide(versionKey);
    if (!guide) {
      closeGuide();
      return warn(`## no guide found close onboarding guide!`);
    }
    if (!hasSeenCurrentGuide()) {
      // Small delay so the page has rendered before the overlay appears.
      const t = setTimeout(() => user && openGuide(0), 600);
      return () => clearTimeout(t);
    }
  }, [autoTrigger, user, openGuide, closeGuide]);

  const skip = () => {
    markGuideAsSeen();
    closeGuide();
  };

  const complete = () => {
    markGuideAsSeen();
    closeGuide();
  };

  return { ...store, skip, complete };
};
